import { useEffect, useMemo, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { isAssetCode, isNetworkId, type AssetCode } from '@onyx/shared';
import { queryClient, useMe } from './lib/queries';
import { ApiError } from './lib/api';
import { LangContext } from './lib/i18n';
import { FiatContext } from './lib/app';
import { usePrefs } from './lib/prefs';
import { back, canGoBack, navigate, useRoute, type Route } from './lib/router';
import { backButton, bootTelegram, onThemeChange, paintChrome, startParam, telegramScheme } from './lib/telegram';
import { TabBar, type Tab } from './components/TabBar';
import { ToastHost } from './components/ui';
import { Home } from './screens/Home';
import { AssetScreen } from './screens/Asset';
import { DepositAddress, DepositPickAsset, DepositPickNetwork } from './screens/Deposit';
import { WithdrawForm, WithdrawPickAsset, WithdrawPickNetwork } from './screens/Withdraw';
import { Send } from './screens/Send';
import { Swap } from './screens/Swap';
import { ChequeCreate, ChequeView, InvoiceCreate, InvoiceView, Payments } from './screens/Payments';
import { ActivityDetail, History } from './screens/History';
import { Settings } from './screens/Settings';
import { Login } from './screens/Login';
import { AdminLog, AdminOverview, AdminReserves, AdminSweeps, AdminUser, AdminUsers, AdminWithdrawals } from './screens/Admin';

bootTelegram();

function useTheme() {
  const prefs = usePrefs();
  const [tgScheme, setTgScheme] = useState(telegramScheme());
  const [osDark, setOsDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => onThemeChange(setTgScheme), []);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setOsDark(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const theme = prefs.theme !== 'auto' ? prefs.theme : (tgScheme ?? (osDark ? 'dark' : 'light'));
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    const ground = getComputedStyle(document.documentElement).getPropertyValue('--ground').trim();
    paintChrome(ground);
    document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', ground));
  }, [theme]);
}

/** Map a route to its screen and the tab it belongs to. */
function resolve(route: Route): { tab: Tab | null; node: React.ReactNode } {
  const [a, b, c, d] = route.segments;
  const asset = (x?: string): AssetCode | null => (x && isAssetCode(x) ? x : null);
  switch (a) {
    case undefined:
      return { tab: 'wallet', node: <Home /> };
    case 'asset':
      return asset(b) ? { tab: null, node: <AssetScreen code={asset(b)!} /> } : { tab: 'wallet', node: <Home /> };
    case 'deposit':
      if (asset(b) && c && isNetworkId(c)) return { tab: null, node: <DepositAddress asset={asset(b)!} network={c} /> };
      if (asset(b)) return { tab: null, node: <DepositPickNetwork asset={asset(b)!} /> };
      return { tab: null, node: <DepositPickAsset /> };
    case 'withdraw':
      if (asset(b) && c && isNetworkId(c)) return { tab: null, node: <WithdrawForm key={`${b}${c}`} asset={asset(b)!} network={c} /> };
      if (asset(b)) return { tab: null, node: <WithdrawPickNetwork asset={asset(b)!} /> };
      return { tab: null, node: <WithdrawPickAsset /> };
    case 'send':
      return { tab: null, node: <Send initial={asset(b) ?? undefined} /> };
    case 'swap': {
      const from = asset(route.query.get('from') ?? undefined) ?? undefined;
      const to = asset(route.query.get('to') ?? undefined) ?? undefined;
      return { tab: 'swap', node: <Swap key={`${from}${to}`} initialFrom={from} initialTo={to} /> };
    }
    case 'pay':
      if (b === 'cheques' && c === 'new') return { tab: null, node: <ChequeCreate /> };
      if (b === 'invoices' && c === 'new') return { tab: null, node: <InvoiceCreate /> };
      if (b === 'cheque' && c) return { tab: null, node: <ChequeView id={c} /> };
      if (b === 'invoice' && c) return { tab: null, node: <InvoiceView id={c} /> };
      return { tab: 'pay', node: <Payments tab={route.query.get('tab') === 'invoices' ? 'invoices' : 'cheques'} /> };
    case 'c':
      return { tab: null, node: <ChequeView id={b ?? ''} /> };
    case 'i':
      return { tab: null, node: <InvoiceView id={b ?? ''} /> };
    case 'history':
      return { tab: 'history', node: <History /> };
    case 'activity':
      return { tab: null, node: <ActivityDetail id={b ?? ''} /> };
    case 'settings':
      return { tab: null, node: <Settings /> };
    case 'admin':
      if (b === 'withdrawals') return { tab: null, node: <AdminWithdrawals /> };
      if (b === 'sweeps') return { tab: null, node: <AdminSweeps /> };
      if (b === 'reserves') return { tab: null, node: <AdminReserves /> };
      if (b === 'users' && c) return { tab: null, node: <AdminUser key={c} id={c} /> };
      if (b === 'users') return { tab: null, node: <AdminUsers /> };
      if (b === 'log') return { tab: null, node: <AdminLog /> };
      return { tab: null, node: <AdminOverview /> };
    default:
      void d;
      return { tab: 'wallet', node: <Home /> };
  }
}

function Shell() {
  useTheme();
  const route = useRoute();
  const me = useMe();
  const { tab, node } = useMemo(() => resolve(route), [route]);

  // Cheque and invoice links arrive as the Mini App start parameter.
  useEffect(() => {
    const p = startParam();
    if (p && /^C[0-9A-Z]{11}$/.test(p)) navigate(`/c/${p}`, { replace: true });
    else if (p && /^I[0-9A-Z]{11}$/.test(p)) navigate(`/i/${p}`, { replace: true });
  }, []);

  useEffect(() => backButton(!tab || canGoBack(), () => back()), [route, tab]);
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [route.path]);

  const unauthorized = me.error instanceof ApiError && me.error.status === 401;
  const lang = me.data?.me.lang ?? (navigator.language.startsWith('ru') ? 'ru' : 'en');
  const fiat = me.data?.me.fiat ?? 'USD';

  return (
    <LangContext.Provider value={lang}>
      <FiatContext.Provider value={fiat}>
        <div className={`app${tab ? ' app--tabbed' : ''}${route.segments[0] === 'admin' ? ' app--wide' : ''}`}>
          {unauthorized ? (
            <Login />
          ) : (
            <div key={route.path} className="route">
              {node}
            </div>
          )}
          {tab && !unauthorized && <TabBar active={tab} />}
          <ToastHost />
        </div>
      </FiatContext.Provider>
    </LangContext.Provider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Shell />
    </QueryClientProvider>
  );
}
